import { createTheme as materialCreateTheme, PaletteMode, ThemeOptions } from '@mui/material';
import { deDE } from '@mui/material/locale';

import { createAccordionTheme } from '../../components/Accordion/Accordion.theme';
import { createButtonTheme } from '../../components/Button/Button.theme';
import { createButtonGroupTheme } from '../../components/ButtonGroup/ButtonGroup.theme';
import { createCardTheme } from '../../components/Card/Card.theme';
import { createCheckboxTheme } from '../../components/Checkbox/Checkbox.theme';
import { createContainerTheme } from '../../components/Container/Container.theme';
import { createDialogTheme } from '../../components/Dialog/Dialog.theme';
import { createDividerTheme } from '../../components/Divider/Divider.theme';
import { createFlyoutTheme } from '../../components/Flyout/Flyout.theme';
import { createIconButtonTheme } from '../../components/IconButton/IconButton.theme';
import { createLinkTheme } from '../../components/Link/Link.theme';
import { createListTheme } from '../../components/List/List.theme';
import { createLoadingDotsTheme } from '../../components/LoadingDots/LoadingDots.theme';
import { createPillTheme } from '../../components/Pill/Pill.theme';
import { createRadioGroupTheme } from '../../components/RadioGroup/RadioGroup.theme';
import { createSelectFieldTheme } from '../../components/SelectField/SelectField.theme';
import { createSelectFieldItemTheme } from '../../components/SelectFieldItem/SelectFieldItem.theme';
import { createStepperTheme } from '../../components/StepBar/StepBar.theme';
import { createTextFieldTheme } from '../../components/TextField/TextField.theme';
import { createToggleSwitchTheme } from '../../components/ToggleSwitch/ToggleSwitch.theme';
import { createTooltipTheme } from '../../components/TooltipButton/Tooltip.theme';
import { createTypographyTheme } from '../../components/Typography/Typography.theme';
import type { Theme } from '../themes';
import { tokenPaletteMain } from './primitives/tokenPalette';

export const options = {
  // index: 0  1  2  3  4   5   6   7   8   9   10  11  12  13  14, 15
  spacing: [0, 3, 6, 9, 12, 18, 24, 30, 36, 48, 60, 72, 90, 96, 120, 150],
  breakpoints: {
    values: {
      xs: 0, // begin of mobile
      sm: 720, // end of mobile / begin of tablet
      md: 1080, // intermediate tablet == if6 begin
      lg: 1440, // end of tablet / begin of desktop
      xl: 1600, // end of desktop, ab hier maxWidth=xl

      // TEXT
      text: 980,
    },
  },
  shape: {
    borderRadius: 6,
  },
  palette: {
    // default color-tokens
    mode: 'light' as PaletteMode,
    primary: {
      dark: '#D10000', // SPK Rot Dark
      darker: '#B60000',
      main: '#EE0000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      dark: '#292929',
      darker: '#000000',
      main: '#BEBEBE',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#B60000',
    },
    text: {
      primary: '#000000',
      secondary: '#444444',
      headline: '#000000',
      link: '#EE0000',
      overline: '#EE0000',
      disabled: '#6E6E6E',
      breadcrumb: '#444444',
      linda: '#444444',
      unit: '#949494',
    },
    divider: '#E3E3E3',
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    action: {
      active: '#444444',
      hover: '#f0f0f0',
      focus: '#0047FF',
      hint: '#565656',
      border: '#BEBEBE',
    },
    grey: {
      dark: '#444444',
      main: '#565656',
      light: '#BEBEBE',
      contrastText: '#FFFFFF',
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#F0F0F0',
      300: '#E3E3E3',
      400: '#BEBEBE',
      500: '#AEAEAE',
      600: '#959595',
      700: '#6E6E6E',
      800: '#565656',
      900: '#444444',
      A100: '#292929',
    },
    // custom color-tokens
    red: {
      dark: '#D10000',
      main: '#B60000',
      contrastText: '#FFFFFF',
    },
    green: {
      main: '#009864',
      secondary: '#009864',
      light: '#8CD000',
      contrastText: '#FFFFFF',
      tonal: 'rgba(140,208,0, 0.16)', //'#8CD000',
      highlight: '#00724B',
    },
    orange: {
      main: '#FF8F00',
    },
    border: {
      main: '#959595',
      hover: '#292929',
    },
    topline: {
      opened: '#009864',
      closed: '#D10000',
      information: '#565656',
    },
    advice: {
      info: '#F1F5FF',
      success: '#EFF7F5',
    },
    calculators: {
      green: '#009864',
      blue: '#2C57D2',
      orange: '#FF8F00',
      darkOrange: '#D14800',
      purple: '#9B348E',
      grey: '#E3E3E3',
      fishBlue: '#0941D1',
      blueAtoll: '#00ACD3',
      turquoise: '#09bdd1',
    },
    map: {
      edge: '#FFFFFF',
      fill: '#0047FF',
      line: '#0941D1',
    },
    button: {
      primary: {
        background: '#EE0000',
        backgroundActive: '#B60000',
        backgroundHover: '#D10000',
        backgroundDisabled: '#E3E3E3',
        color: '#FFFFFF',
        colorActive: '#FFFFFF',
        colorHover: '#FFFFFF',
        colorFocus: '#FFFFFF',
        colorDisabled: '#999999',
        shadow: '#EE0000',
        shadowActive: '#B60000',
        shadowHover: '#D10000',
        shadowFocus: '#0047FF',
        shadowDisabled: '#E3E3E3',
      },
      secondary: {
        background: 'transparent',
        backgroundActive: '#00000006',
        backgroundHover: '#00000003',
        backgroundDisabled: 'transparent',
        color: '#292929',
        colorActive: '#000000',
        colorHover: '#222222',
        colorFocus: '#292929',
        colorDisabled: '#949494',
        shadow: '#565656',
        shadowActive: '#292929',
        shadowHover: '#444444',
        shadowFocus: '#0047FF',
        shadowDisabled: '#949494',
      },
      tertiary: {
        background: 'transparent',
        backgroundActive: '#00000006',
        backgroundHover: '#00000003',
        backgroundDisabled: 'transparent',
        color: '#292929',
        colorActive: '#000000',
        colorHover: '#222222',
        colorFocus: '#292929',
        colorDisabled: '#949494',
        shadow: 'transparent',
        shadowActive: '#00000006',
        shadowHover: '#00000003',
        shadowFocus: '#0047FF',
        shadowDisabled: 'transparent',
      },
    },
    buttonGroup: {
      backgroundActive: '#009864',
      backgroundHover: '#444444',
      colorDisabled: '#949494',
      borderDisabled: '#E3E3E3',
    },
    tileButton: {
      border: {
        default: '#BEBEBE',
        disabled: '#E3E3E3',
        active: '#949494',
        selected: '#8CD000',
      },
      color: {
        disabled: '#949494',
      },
      text: '#292929',
      icon: '#444444',
      statusIcon: '#8CD000',
    },
    energyClass: {
      'a+': '#37AE80',
      a: '#5CB854',
      b: '#90D15D',
      c: '#B1DF5F',
      d: '#FADC39',
      e: '#FFC433',
      f: '#FF8527',
      g: '#FF6633',
      h: '#FF3F33',
    },
    search: {
      input: {
        backgroundDefault: '#F5F5F5',
        backgroundActive: '#F0F0F0',
      },
      list: {
        backgroundHover: '#F0F0F0',
      },
      pills: {
        outlineHover: '#949494',
      },
      checkbox: {
        backgroundHover: '#F0F0F0',
        iconSelected: '#000000',
      },
      dropdown: {
        label: '#565656',
      },
    },
    brand: {
      main: '#EE0000',
    },
    ...tokenPaletteMain,
  },
  typography: {
    fontFamily: 'Sparkasse Web',
    fontSize: 12,
    fontWeightBold: 700,
    fontWeightLight: 200,
    fontWeightMedium: 400,
    fontWeightRegular: 400,
    root: {
      textDecoration: 'none',
    },
    h1: {
      fontSize: 60,
      lineHeight: 1.08,
    },
    h2: {
      fontSize: 42,
      lineHeight: 1.43,
    },
    h3: {
      fontSize: 36,
      lineHeight: 1.33,
    },
    h4: {
      fontSize: 24,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: 20,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: 18,
      lineHeight: 1.44,
    },
    overline: {
      fontSize: 18,
      lineHeight: 1.44,
    },
    body1: {
      fontSize: 24,
      lineHeight: 1.33,
    },
    body2: {
      fontSize: 18,
      lineHeight: 1.44,
    },
    button: {
      fontSize: 16,
      lineHeight: 1.62,
    },
  },
};

export function createTheme(...overrideTheme: ThemeOptions[]): Theme {
  const baseTheme = materialCreateTheme(options, ...overrideTheme);
  const typedTheme = materialCreateTheme(options, ...overrideTheme, createTypographyTheme(baseTheme));

  return materialCreateTheme(
    options,
    ...overrideTheme,
    createTypographyTheme(baseTheme),
    createButtonTheme(baseTheme),
    createContainerTheme(baseTheme),
    createIconButtonTheme(baseTheme),
    createLinkTheme(baseTheme),
    createPillTheme(typedTheme),
    createTextFieldTheme(typedTheme),
    createToggleSwitchTheme(baseTheme),
    createCardTheme(baseTheme),
    createDialogTheme(baseTheme),
    createDividerTheme(typedTheme),
    createAccordionTheme(baseTheme),
    createSelectFieldTheme(typedTheme),
    createSelectFieldItemTheme(typedTheme),
    createButtonGroupTheme(baseTheme),
    createListTheme(baseTheme),
    createRadioGroupTheme(typedTheme),
    createFlyoutTheme(baseTheme),
    createCheckboxTheme(baseTheme),
    createLoadingDotsTheme(),
    createTooltipTheme(typedTheme),
    createStepperTheme(typedTheme),
    deDE
  );
}

export const theme: Theme = createTheme();
